import { lazy, Suspense, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { HeaderPlaceholder, Main, Wrapper } from './content.styles';

import ErrorBoundary from '../../error_boundary';
import LoadingText from '../../loading_text';
import ScrollToTop from '../../scroll_to_top';
import Title from '../../title';
import Header from './header';
import AcceptTermsAndConditionsPage from './home/card_subscription/accept_terms_and_conditions';
import AppConsentsPage from './home/card_subscription/app_consents';
import OffersPage from './home/card_subscription/offers';
import PlacesPage from './home/card_subscription/places';
import RewardReceivedPage from './home/card_subscription/reward_received';
import RewardsPage from './home/card_subscription/rewards';
import SelectFavoritePlacePage from './home/card_subscription/select_favorite_place';
import TermsAndConditionsPage from './home/card_subscription/terms_and_conditions';

import { vendorCardState } from '../../../store';

const HomePage = lazy(() => import('./home'));
const MapPage = lazy(() => import('./map'));
const FavoritesPage = lazy(() => import('./favorites'));
const FlavorsPage = lazy(() => import('./flavors'));
const InboxPage = lazy(() => import('./inbox'));
const ChallengesPage = lazy(() => import('./challenges'));
const ProfilePage = lazy(() => import('./profile'));
const CameraPage = lazy(() => import('./camera'));
const RedeemPage = lazy(() => import('./redeem'));

const PWAPopover = lazy(() => import('./pwa_popover'));

export default function Content() {
  const location = useLocation();
  const wrapper = useRef<HTMLDivElement>(null);

  const card = useAtomValue(vendorCardState);

  const hasTermsAndConditions = Boolean(card.terms_and_conditions);

  const isMain = location.pathname === '/';

  return (
    <>
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <PWAPopover />
        </Suspense>
      </ErrorBoundary>
      <Wrapper isMain={isMain} ref={wrapper}>
        <Title />
        <ScrollToTop wrapper={wrapper} />
        <Routes>
          <Route element={<Header />} path="" />
          <Route element={<HeaderPlaceholder />} path="favorites/*" />
          <Route element={<HeaderPlaceholder />} path="flavors" />
          <Route element={<HeaderPlaceholder />} path="flavors/available" />
          <Route element={<HeaderPlaceholder />} path="flavors/favorited" />
          <Route element={<HeaderPlaceholder />} path="challenges" />
          <Route element={<HeaderPlaceholder />} path="inbox" />
        </Routes>
        <Main>
          <Suspense fallback={<LoadingText />}>
            <Routes>
              <Route element={<HomePage />} path="" />
              <Route element={<MapPage />} path="map/*" />
              <Route element={<FavoritesPage />} path="favorites/*" />
              <Route element={<FlavorsPage />} path="flavors/*" />
              <Route element={<InboxPage />} path="inbox/*" />
              <Route element={<ChallengesPage />} path="challenges/*" />
              <Route element={<ProfilePage />} path="profile/*" />
              <Route element={<CameraPage />} path="camera/*" />
              <Route element={<RedeemPage />} path="redeem/*" />
              <Route element={<OffersPage />} path="offers/*" />
              <Route element={<PlacesPage />} path="places/*" />
              <Route element={<RewardsPage />} path="rewards/*" />
              <Route element={<RewardReceivedPage />} path="reward_received/*" />
              <Route element={<AppConsentsPage />} path="app_consents/*" />
              {hasTermsAndConditions ? (
                <Route
                  element={<AcceptTermsAndConditionsPage />}
                  path="accept_terms_and_conditions/*"
                />
              ) : null}
              {hasTermsAndConditions ? (
                <Route element={<TermsAndConditionsPage />} path="terms_and_conditions/*" />
              ) : null}
              <Route element={<SelectFavoritePlacePage />} path="select_favorite_place/*" />
              <Route element={<Navigate to="/" />} path="*" />
            </Routes>
          </Suspense>
        </Main>
      </Wrapper>
    </>
  );
}
